/**
 * DRM capability detection — answers "can protected playback work in this
 * browser at all?" so a host can show an accurate message instead of a black
 * player (e.g. iOS Chrome/Firefox/Edge: WKWebView exposes the EME API surface
 * but grants no CDM — Widevine does not exist on iOS and WebKit withholds
 * FairPlay from third-party browsers).
 *
 * Design rules (each encodes a real failure mode):
 *  - PROBE, don't UA-sniff: the probe is ground truth for playability. UA
 *    strings are spoofable, iPadOS masquerades as macOS, and in-app WebViews
 *    carry no browser token. Hosts may still use the UA to WORD a message —
 *    never to gate playback.
 *  - `requestMediaKeySystemAccess` resolving is NOT proof: some platforms
 *    resolve access and then fail `createMediaKeys()` — both must succeed.
 *  - Bounded: some WebView builds never settle the access promise; without a
 *    timeout the caller's UI hangs on "checking…".
 *  - Lazy by contract: the EME spec allows the call to have user-visible
 *    effects (consent prompts, CDM provisioning downloads). Only call this
 *    once you know the content is DRM-protected — never on page load.
 *  - Per-system configs must mirror what the engine will actually request:
 *    Widevine uses the SW_SECURE_* pins; FairPlay must NOT (WebKit's
 *    supportedRobustnesses() for FPS is { '' } — a Widevine-shaped probe
 *    false-negatives on Safari).
 *  - `SecurityError` is reported separately: a cross-origin iframe missing
 *    allow="encrypted-media" (or an https embed in an http parent) is an
 *    embedding misconfiguration, not an unsupported browser.
 */
import type { DrmSystem } from '../types';
import { withVideoRobustnessFallbacks } from './cdm';

export type DrmSupportVerdict =
  /** At least one probed system produced working MediaKeys. */
  | 'supported'
  /** EME exists but no probed system yields a CDM (e.g. iOS third-party browsers). */
  | 'no-cdm'
  /** Not a secure context — EME is unavailable by spec (http://<LAN-IP> dev, etc.). */
  | 'insecure-context'
  /** SecurityError: permissions-policy / iframe allow="encrypted-media" missing. */
  | 'blocked-by-policy'
  /** The access promise never settled within timeoutMs (some WebViews). */
  | 'probe-timeout';

export interface DrmSupportResult {
  verdict: DrmSupportVerdict;
  /** The first system that probed as working (when verdict is 'supported'). */
  system?: DrmSystem;
  /** Per-system outcome, for diagnostics/support bundles. */
  detail: Partial<Record<DrmSystem, string>>;
}

export interface DetectDrmSupportOptions {
  /** Systems to probe, in order. Default: both. */
  systems?: DrmSystem[];
  /** OVERALL budget for the whole call before concluding 'probe-timeout' —
   *  shared across every probed system/stage, not per probe. Default 5000ms. */
  timeoutMs?: number;
}

const DEFAULT_PROBE_TIMEOUT_MS = 5_000;

const VIDEO_MP4 = 'video/mp4; codecs="avc1.42E01E"';
const AUDIO_MP4 = 'audio/mp4; codecs="mp4a.40.2"';

/** Key-system strings per DRM system. EXACTLY what the hls.js engine
 *  configures — the probe predicts the ENGINE, not abstract capability, so a
 *  key system the engine never requests (e.g. legacy `com.apple.fps.1_0`,
 *  which hls.js has no fallback to) must not produce a 'supported' verdict. */
const KEY_SYSTEMS: Record<DrmSystem, string[]> = {
  widevine: ['com.widevine.alpha'],
  fairplay: ['com.apple.fps'],
};

/** One config per system, mirroring the engine's EFFECTIVE access request
 *  (hls.ts drmSystemOptions → hls.js builds a single configuration):
 *  Widevine carries the SW_SECURE_* pins, FairPlay the empty robustness
 *  WebKit requires, and BOTH pin encryptionScheme cbcs exactly as the engine
 *  does. (Not literally byte-for-byte: initDataTypes ordering and the
 *  canonical codec strings differ from hls.js's manifest-derived ones, and
 *  spec-default fields are omitted — none of which can flip a verdict.) A
 *  WEAKER probe config (pin-less, scheme-less) would report 'supported' on
 *  CDMs the engine's stricter request then fails on — the exact
 *  optimistic-message-then-black-player this API exists to prevent. */
function probeConfigs(system: DrmSystem): MediaKeySystemConfiguration[] {
  if (system === 'fairplay') {
    return [
      {
        initDataTypes: ['sinf', 'skd', 'cenc'],
        videoCapabilities: [{ contentType: VIDEO_MP4, robustness: '', encryptionScheme: 'cbcs' }],
        audioCapabilities: [{ contentType: AUDIO_MP4, robustness: '', encryptionScheme: 'cbcs' }],
      },
    ];
  }
  // Laddered through the same helper the engine's access hook uses, so the
  // probe keeps mirroring the EFFECTIVE request. Without this the probe would
  // be STRICTER than the engine and report 'no-cdm' for a device the engine
  // then plays on — the same false verdict this file exists to prevent, only
  // inverted.
  return withVideoRobustnessFallbacks('com.widevine.alpha', [
    {
      initDataTypes: ['cenc'],
      videoCapabilities: [
        { contentType: VIDEO_MP4, robustness: 'SW_SECURE_DECODE', encryptionScheme: 'cbcs' },
      ],
      audioCapabilities: [
        { contentType: AUDIO_MP4, robustness: 'SW_SECURE_CRYPTO', encryptionScheme: 'cbcs' },
      ],
    },
  ]);
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('probe-timeout')), ms);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e) => {
        clearTimeout(t);
        reject(e);
      },
    );
  });
}

/**
 * Probe whether DRM playback can work here. Resolves — never rejects.
 *
 * Call it lazily (only for content whose descriptor says `drmEnabled`), CACHE
 * the result yourself (probes are not memoized, and the EME spec allows each
 * call to show consent prompts / trigger CDM downloads), and treat
 * 'supported' as necessary-not-sufficient: the licence server, CORS
 * onboarding and content packaging still have to cooperate.
 */
export async function detectDrmSupport(
  opts: DetectDrmSupportOptions = {},
): Promise<DrmSupportResult> {
  const systems = opts.systems ?? (['widevine', 'fairplay'] as DrmSystem[]);
  const timeoutMs = opts.timeoutMs ?? DEFAULT_PROBE_TIMEOUT_MS;
  const detail: Partial<Record<DrmSystem, string>> = {};

  if (typeof window !== 'undefined' && window.isSecureContext === false) {
    return { verdict: 'insecure-context', detail };
  }
  const rmksa =
    typeof navigator !== 'undefined'
      ? navigator.requestMediaKeySystemAccess?.bind(navigator)
      : undefined;
  if (!rmksa) {
    // Secure context but no EME entry point at all (very old browser, or an
    // embedder stripped it) — indistinguishable from no CDM for the caller.
    return { verdict: 'no-cdm', detail };
  }

  let sawTimeout = false;
  let sawSecurityError = false;
  // One WALL-CLOCK deadline for the whole call: stages consume the remaining
  // budget, so N systems × 2 stages can never stack into N×2 × timeoutMs of
  // "checking…" UI hang.
  const deadline = Date.now() + timeoutMs;
  const remaining = (): number => Math.max(1, deadline - Date.now());

  for (const system of systems) {
    const keySystems = KEY_SYSTEMS[system];
    if (!keySystems) {
      // Untyped callers can pass arbitrary strings — record, don't reject
      // (the documented contract is "resolves, never rejects").
      detail[system] = 'unknown system';
      continue;
    }
    for (const keySystem of keySystems) {
      if (Date.now() >= deadline) {
        sawTimeout = true;
        detail[system] ??= `${keySystem}: probe-timeout`;
        break;
      }
      try {
        const access = await withTimeout(rmksa(keySystem, probeConfigs(system)), remaining());
        // Access alone is not proof — the CDM must actually instantiate.
        await withTimeout(access.createMediaKeys(), remaining());
        detail[system] = `ok (${keySystem})`;
        return { verdict: 'supported', system, detail };
      } catch (err) {
        // Read name/message structurally: DOMException is not `instanceof
        // Error` in every runtime (jsdom, older WebKit) and the distinction
        // matters — SecurityError must not degrade into 'no-cdm'. Prefer the
        // message when the name is the generic 'Error' so a timeout is
        // legible in support bundles.
        const e = err as { name?: string; message?: string } | undefined;
        const name = (e?.name && e.name !== 'Error' ? e.name : e?.message) || String(err);
        detail[system] = `${keySystem}: ${name}`;
        if (e?.message === 'probe-timeout') sawTimeout = true;
        if (e?.name === 'SecurityError') sawSecurityError = true;
        // NotSupportedError (no CDM for this key system) → try the next one.
      }
    }
  }

  if (sawSecurityError) return { verdict: 'blocked-by-policy', detail };
  if (sawTimeout) return { verdict: 'probe-timeout', detail };
  return { verdict: 'no-cdm', detail };
}
